import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { TagColor, type Lookup, type LookupInput, type TagStyle } from '@ncc/shared';
import { ApiError, api } from '../lib/api';

type Endpoint = {
  list: () => Promise<Lookup[]>;
  create: (input: LookupInput) => Promise<Lookup>;
  update: (id: number, input: LookupInput) => Promise<Lookup>;
  remove: (id: number) => Promise<{ ok: true }>;
};

const SECTIONS: Array<{
  key: string;
  title: string;
  queryKey: string;
  endpoint: Endpoint;
  withStyle?: boolean;
}> = [
  {
    key: 'brand',
    title: 'Brands',
    queryKey: 'brands',
    endpoint: {
      list: api.listBrands,
      create: api.createBrand,
      update: api.updateBrand,
      remove: api.deleteBrand,
    },
  },
  {
    key: 'type',
    title: 'Inventory types',
    queryKey: 'inventory-types',
    endpoint: {
      list: api.listInventoryTypes,
      create: api.createInventoryType,
      update: api.updateInventoryType,
      remove: api.deleteInventoryType,
    },
  },
  {
    key: 'tag',
    title: 'Tags',
    queryKey: 'tags',
    withStyle: true,
    endpoint: {
      list: api.listTags,
      create: api.createTag,
      update: api.updateTag,
      remove: api.deleteTag,
    },
  },
  {
    key: 'part_category',
    title: 'Part categories',
    queryKey: 'part-categories',
    endpoint: {
      list: api.listPartCategories,
      create: api.createPartCategory,
      update: api.updatePartCategory,
      remove: api.deletePartCategory,
    },
  },
  {
    key: 'product_category',
    title: 'Product categories',
    queryKey: 'product-categories',
    endpoint: {
      list: api.listProductCategories,
      create: api.createProductCategory,
      update: api.updateProductCategory,
      remove: api.deleteProductCategory,
    },
  },
];

export function LookupsPage() {
  return (
    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr 1fr' }}>
      {SECTIONS.map((s) => (
        <LookupTable
          key={s.key}
          title={s.title}
          queryKey={s.queryKey}
          endpoint={s.endpoint}
          withStyle={s.withStyle}
        />
      ))}
    </div>
  );
}

function LookupTable({
  title,
  queryKey,
  endpoint,
  withStyle,
}: {
  title: string;
  queryKey: string;
  endpoint: Endpoint;
  // Tags only: the Promotion / Feature picker and the chip colour picker.
  withStyle?: boolean;
}) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: [queryKey], queryFn: endpoint.list });
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<Lookup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] });

  // Without this every failure here was invisible: the mutation rejected, no
  // handler ran, and the row or input just sat there looking untouched. A
  // duplicate name is the common case and now says so.
  const describe = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 409) {
      const detail = err.detail as { existing_name?: string } | null;
      return detail?.existing_name
        ? `“${detail.existing_name}” already exists.`
        : 'That name already exists.';
    }
    return err instanceof Error ? err.message : fallback;
  };

  const create = useMutation({
    mutationFn: endpoint.create,
    onSuccess: () => {
      setName('');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(describe(err, 'Could not add that.')),
  });
  const update = useMutation({
    mutationFn: (args: { id: number; input: LookupInput }) => endpoint.update(args.id, args.input),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      invalidate();
    },
    onError: (err) => setError(describe(err, 'Could not save that change.')),
  });
  const remove = useMutation({
    mutationFn: endpoint.remove,
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(describe(err, 'Could not delete that.')),
  });

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <div className="toolbar">
        <input
          placeholder="Add new…"
          maxLength={100}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) create.mutate({ name: name.trim() });
          }}
        />
        <button
          className="primary"
          disabled={!name.trim() || create.isPending}
          onClick={() => create.mutate({ name: name.trim() })}
        >
          Add
        </button>
      </div>
      {error ? (
        <div className="error-text" role="alert">
          {error}
        </div>
      ) : null}
      <table style={{ marginTop: '0.75rem' }}>
        <tbody>
          {(list.data ?? []).map((item) => (
            <tr key={item.id}>
              <td>
                {editing?.id === item.id ? (
                  <input
                    autoFocus
                    defaultValue={item.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== item.name)
                        update.mutate({ id: item.id, input: { name: v } });
                      else setEditing(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') setEditing(null);
                    }}
                  />
                ) : (
                  <span onDoubleClick={() => setEditing(item)} style={{ cursor: 'pointer' }}>
                    {item.name}
                  </span>
                )}
              </td>
              {withStyle ? (
                <td style={{ width: 120 }}>
                  <select
                    aria-label={`Kind of tag for ${item.name}`}
                    title="Feature tags also show beside the price on the trailer's page"
                    value={item.style ?? 'promo'}
                    disabled={update.isPending}
                    onChange={(e) =>
                      update.mutate({
                        id: item.id,
                        input: {
                          name: item.name,
                          sort_order: item.sort_order,
                          style: e.target.value as TagStyle,
                        },
                      })
                    }
                  >
                    <option value="promo">Promotion</option>
                    <option value="feature">Feature</option>
                  </select>
                </td>
              ) : null}
              {withStyle ? (
                <td style={{ width: 100 }}>
                  <select
                    aria-label={`Colour of tag ${item.name}`}
                    title="Colour of this tag's chip on the site"
                    value={item.color ?? (item.style === 'feature' ? 'green' : 'yellow')}
                    disabled={update.isPending}
                    onChange={(e) =>
                      update.mutate({
                        id: item.id,
                        input: {
                          name: item.name,
                          sort_order: item.sort_order,
                          color: e.target.value as TagColor,
                        },
                      })
                    }
                  >
                    {TagColor.options.map((c) => (
                      <option key={c} value={c}>
                        {c.charAt(0).toUpperCase() + c.slice(1)}
                      </option>
                    ))}
                  </select>
                </td>
              ) : null}
              <td style={{ width: 60, textAlign: 'right' }}>
                <button
                  className="ghost"
                  title="Delete"
                  onClick={() => {
                    if (confirm(`Delete "${item.name}"? Inventory keeps the text but loses the link.`))
                      remove.mutate(item.id);
                  }}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
